import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  TrendingUp,
  Store,
  LocalOffer,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { url } from '../constants';

// ✅ Helper to fix the missing image path and link it to the backend server
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const TrendingDeals = () => {
  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLocationBased, setIsLocationBased] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [favoritesLoading, setFavoritesLoading] = useState({});

  const { userLocation } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load categories from localStorage
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          setCategories(parsedCategories);
        }
      } catch (error) {
        console.error('❌ Error parsing categories:', error);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  useEffect(() => {
    fetchTrendingDeals();
  }, [userLocation]);

  const fetchTrendingDeals = async () => {
    setLoading(true);
    setError('');

    try {
      let endpoint = `${url}/deals/trending`;

      if (userLocation && userLocation.city) {
        endpoint = `${url}/deals/trending?city=${encodeURIComponent(userLocation.city)}`;
        setIsLocationBased(true);
        console.log('🎯 Fetching location-based trending deals for:', userLocation.city);
      } else {
        setIsLocationBased(false);
        console.log('🌍 Fetching all-India trending deals');
      }

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch trending deals`);
      }

      const data = await response.json();
      console.log(`✅ Fetched ${data.length} trending deals`);
      
      if (Array.isArray(data)) {
        setDeals(data);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (error) {
      console.error('❌ Error fetching trending deals:', error);
      setError(error.message || 'Failed to load trending deals');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  // Get category color from saved categories list
  const getCategoryColor = (categoryName) => {
    const savedCategory = categories.find(cat => 
      cat.cate_name === categoryName || 
      categoryName.includes(cat.cate_name) ||
      cat.cate_name.includes(categoryName)
    );
    
    if (savedCategory && savedCategory.cate_color) {
      return savedCategory.cate_color;
    }

    const colorMap = {
      'Electronics': '#FF6B6B',
      'Fashion': '#4ECDC4',
      'Food': '#45B7D1',
      'Grocery': '#96CEB4',
      'Travel': '#FECA57',
      'Home': '#FF9FF3',
      'Beauty': '#54A0FF',
      'Books': '#5F27CD',
      'Sports': '#00D2D3',
      'Groceries': '#1a7f3a',
      'Food & Dining': '#FF6B6B',
      'Shopping & Retail': '#4ECDC4',
    };
    
    return colorMap[categoryName] || '#1a7f3a';
  };

  // Much darker shade for category chip
  const getDarkCategoryChipColor = (categoryName) => {
    const color = getCategoryColor(categoryName);
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkerR = Math.max(0, Math.floor(r * 0.6));
    const darkerG = Math.max(0, Math.floor(g * 0.6));
    const darkerB = Math.max(0, Math.floor(b * 0.6));
    
    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  };

  const handleToggleFavorite = async (dealId, e) => {
    e.stopPropagation();
    
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    const isFav = favorites.includes(dealId);
    setFavoritesLoading(prev => ({ ...prev, [dealId]: true }));
    
    let updatedFavorites;
    if (isFav) {
      updatedFavorites = favorites.filter(fav => fav !== dealId);
    } else {
      updatedFavorites = [...favorites, dealId];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem('userFavorites', JSON.stringify(updatedFavorites));

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isFav 
        ? `${url}/deals/remove-favorite` 
        : `${url}/deals/add-favorite`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deal_id: dealId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update favorites');
      }

      setSnackbar({
        open: true,
        message: isFav ? 'Removed from favorites' : 'Added to favorites',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      setFavorites(favorites);
      localStorage.setItem('userFavorites', JSON.stringify(favorites));
      
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update favorites',
        severity: 'error'
      });
    } finally {
      setFavoritesLoading(prev => {
        const newState = { ...prev };
        delete newState[dealId];
        return newState;
      });
    }
  };

  const isFavorite = (dealId) => favorites.includes(dealId);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#1a7f3a' }} />
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: isMobile ? 2 : 4,
          px: isMobile ? 2 : 0,
        }}>
          <Box>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'} 
              gutterBottom 
              fontWeight={700} 
              sx={{ color: '#4B5563' }}
            >
              Trending Deals
            </Typography>
            <Typography variant={isMobile ? 'caption' : 'body1'} color="text.secondary">
              {isLocationBased && userLocation ? (
                <>Hottest deals in <strong>{userLocation.city}</strong></>
              ) : (
                <>Top picks from across India</>
              )}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            endIcon={<TrendingUp />}
            onClick={() => navigate('/deals')}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: '#1a7f3a',
              borderColor: '#1a7f3a',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#1a7f3a',
                bgcolor: 'rgba(26, 127, 58, 0.04)',
              },
            }}
          >
            View All
          </Button>
        </Box>

        {/* Deals Grid */}
        {deals.length > 0 ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: isMobile ? 0 : 2,
                mb: 4,
              }}
            >
              {deals.slice(0, 12).map((deal, index) => {
                const dealId = deal._id || deal.id || `deal-${index}`;
                const isLoadingFav = favoritesLoading[dealId];
                const darkChipColor = getDarkCategoryChipColor(deal.category);

                // Attempt to grab deal_url, fallback to image_url, or show placeholder
                const rawImageUrl = deal.deal_url || deal.image_url;
                const finalImageUrl = rawImageUrl 
                  ? getImageUrl(rawImageUrl) 
                  : 'https://via.placeholder.com/400x300?text=No+Image';

                return (
                  <Card
                    key={dealId}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: isMobile ? 0 : 1,
                      border: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderRight: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderBottom: isMobile ? '2px solid #E5E7EB' : 'none',
                      '&:hover': {
                        boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                        transform: isMobile ? 'none' : 'translateY(-4px)',
                      },
                    }}
                    onClick={() => navigate(`/deal/${dealId}`)}
                  >
                    {/* ✅ Image Container with Object-Fit Cover */}
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height={isMobile ? "140" : "180"}
                        image={finalImageUrl}
                        alt={deal.deal_name}
                        sx={{
                          objectFit: 'cover',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      />
                      
                      {/* RED Discount Chip - Top Right */}
                      <Chip
                        label={`${deal.percentage_discount}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: isMobile ? 8 : 12,
                          right: isMobile ? 8 : 12,
                          fontWeight: 700,
                          bgcolor: '#FF4444',
                          color: 'white',
                          fontSize: isMobile ? '0.65rem' : '0.7rem',
                          height: isMobile ? 20 : 24,
                          boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                          zIndex: 3,
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                      {/* Dark Category Chip */}
                      <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 0.5 : 1 }}>
                        <Chip
                          label={deal.category}
                          size="small"
                          sx={{
                            bgcolor: darkChipColor,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            height: isMobile ? 18 : 24,
                            width: '100%',
                            justifyContent: 'center',
                          }}
                        />
                      </Stack>

                      {/* ✅ Deal Title moved into CardContent for readability */}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#212121',
                          fontSize: isMobile ? '0.8rem' : '0.95rem',
                        }}
                      >
                        {deal.deal_name}
                      </Typography>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Store sx={{ fontSize: 12, color: '#878787' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.vendor_name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ 
                            color: '#1a7f3a',
                            fontSize: isMobile ? '0.85rem' : '1rem',
                          }}
                        >
                          ₹{deal.discounted_price}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: 'line-through',
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                          }}
                        >
                          ₹{deal.original_price}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <LocationOn sx={{ fontSize: 18, color: '#1a7f3a' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.address}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          onClick={(e) => !isLoadingFav && handleToggleFavorite(dealId, e)}
                          sx={{ 
                            cursor: isLoadingFav ? 'default' : 'pointer',
                            transition: 'all 0.3s ease', 
                            '&:hover': { opacity: isLoadingFav ? 1 : 0.7 },
                            minWidth: '60px',
                          }}
                        >
                          {isLoadingFav ? (
                            <CircularProgress size={isMobile ? 16 : 20} sx={{ color: '#1a7f3a' }} />
                          ) : isFavorite(dealId) ? (
                            <Favorite fontSize="small" sx={{ color: '#FF6B6B', fontSize: isMobile ? 16 : 20 }} />
                          ) : (
                            <FavoriteBorder fontSize="small" sx={{ color: 'text.secondary', fontSize: isMobile ? 16 : 20 }} />
                          )}
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                          >
                            {isLoadingFav ? 'Saving...' : isFavorite(dealId) ? 'Saved' : 'Save'}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/deal/${dealId}`);
                          }}
                          sx={{
                            borderColor: '#1a7f3a',
                            color: '#1a7f3a',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isMobile ? '0.65rem' : '0.8rem',
                            py: isMobile ? 0.4 : 0.75,
                            minHeight: isMobile ? 28 : 36,
                            px: isMobile ? 1 : 2,
                            '&:hover': {
                              borderColor: '#1a7f3a',
                              bgcolor: '#E8F5E9',
                            },
                          }}
                        >
                          View
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            <Box sx={{ textAlign: 'center', px: isMobile ? 2 : 0 }}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                endIcon={<LocalOffer />}
                onClick={() => navigate('/deals')}
                sx={{
                  bgcolor: '#1a7f3a',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': { bgcolor: '#0f5328' },
                }}
              >
                Explore All Deals
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, px: isMobile ? 2 : 0 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No trending deals found
              {isLocationBased && ` in ${userLocation.city}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try changing your location or check back later
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 4, mx: isMobile ? 2 : 0 }}>
            {error}
          </Alert>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrendingDeals;